import qs from 'qs';

import {
  ChangeLogFilters,
  ClusteringRequest,
  ClusteringRunResponse,
  ClusteringsResponse,
  ClustersResponse,
  LereAbandonedCarts,
  SurchargeSuggestionDB,
  SurchargeSuggestionFilters,
} from '~/components/LERE/types';

import { json_headers, request } from './common';

function basePath() {
  return window.configs.API_HOST + '/api/recommendation';
}

function sendTPFMTestEmail(campaign: string, emailRecipient: string, reuseLastRecipientEmail: boolean) {
  return request(`${basePath()}/operations/test_trigger`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({
      campaign: campaign,
      recipient: emailRecipient,
      reuse_last_recipient_email: reuseLastRecipientEmail,
    }),
  });
}

function clearUserInteractions(params: { userId?: string; domainUserId?: string }) {
  return request(`${basePath()}/user-interactions/clear`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({
      le_user_id: params.userId,
      domain_user_id: params.domainUserId,
    }),
  });
}

function getUserInteractions(params: { userId?: string; domainUserId?: string }) {
  const queryParams = {
    le_user_id: params.userId,
    domain_user_id: params.domainUserId,
  } as Record<string, string | undefined>;

  return request(`${basePath()}/user-interactions/all?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function getChangeLog(page: number, limit = 5, filters?: ChangeLogFilters) {
  const queryParams = {
    page,
    limit,
    property_name: filters?.propertyName,
    room_type_name: filters?.roomTypeName,
    actioned_at_start: filters?.actionedDateRange?.start,
    actioned_at_end: filters?.actionedDateRange?.end,
    status: filters?.status,
    enforce_room_type_granularity: filters?.enforceRoomTypeGranularity,
    only_charters: filters?.onlyCharters,
  };

  return request(`${basePath()}/dynamic_pricing/?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function getLatestSurchargeSuggestions(page: number, limit = 10, filters?: SurchargeSuggestionFilters) {
  const queryParams = {
    page,
    limit,
    aggregate: filters?.aggregate,
    offer_id: filters?.offerIdEquals,
    property_name: filters?.propertyNameLike,
    room_type_name: filters?.roomTypeNameLike,
    internal_surcharge: filters?.internalSurchargeEquals,
    sales_gte: filters?.salesGte,
    allocated_gte: filters?.allocatedGte,
    suggestion_type: filters?.suggestionType,
    confidence: filters?.confidence,
    sortby: filters?.sortBy,
    ascending: filters?.ascending,
    only_charters: filters?.onlyCharters,
  };

  // Map the sortby values to the correct API parameters
  if (queryParams.sortby === 'actioned_start_date') {
    queryParams.sortby = 'start_date';
  } else if (queryParams.sortby === 'actioned_end_date') {
    queryParams.sortby = 'end_date';
  }

  return request(`${basePath()}/dynamic_pricing/latest?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function createSurchargeSuggestion(body: Omit<SurchargeSuggestionDB, 'id'>) {
  return request(`${basePath()}/dynamic_pricing`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

function getAbandonedCarts(params: { userId: string; brand: string; region: string }): Promise<LereAbandonedCarts> {
  const { userId, brand, region } = params;

  const queryParams = {
    region,
    brand,
    limit: 50,
    categories: 'sticky_abandoned_carts',
    le_user_id: userId,
  };
  return request(`${basePath()}/enquiry?${qs.stringify(queryParams)}`, {
    method: 'POST',
    body: JSON.stringify({ sticky_abandoned_carts: {} }),
    headers: json_headers,
  }).then((response) => response.result.sticky_abandoned_carts);
}

/**
 * Get clusters for map visualization.
 *
 * @param params.run_id - Explicit run ID to fetch (optional)
 * @param params.as_of - ISO date string (YYYY-MM-DD) to get the most recent run as of that date (optional)
 * @returns Clusters data or null if not found
 */
async function getClusters(params?: { run_id?: string; as_of?: string }): Promise<ClustersResponse> {
  const queryParams = {
    run_id: params?.run_id,
    as_of: params?.as_of,
  };

  const queryString = qs.stringify(queryParams, { skipNulls: true });
  const url = `${basePath()}/admin/clusterings/clusters${queryString ? `?${queryString}` : ''}`;

  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

/**
 * List available clusterings.
 *
 * @param limit - Maximum number of clusterings to return. If not provided, returns all.
 * @returns List of clusterings
 */
async function getClusterings(limit?: number): Promise<ClusteringsResponse> {
  const queryParams = { limit };
  const queryString = qs.stringify(queryParams, { skipNulls: true });
  const url = `${basePath()}/admin/clusterings${queryString ? `?${queryString}` : ''}`;

  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

async function runClustering(body: ClusteringRequest = {}): Promise<ClusteringRunResponse> {
  const url = `${basePath()}/admin/clusterings`;

  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

export default {
  sendTPFMTestEmail,
  getChangeLog,
  getLatestSurchargeSuggestions,
  createSurchargeSuggestion,
  getAbandonedCarts,
  clearUserInteractions,
  getUserInteractions,
  getClusters,
  getClusterings,
  runClustering,
};
